import { useState } from 'react';
import { Code, Brain, Shield, Settings, Play, Database, Zap } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function BareAPILanding() {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Create Bare API Integration
POST /api/v1/sockets/{socket_id}/integrations
{
  "integration_type": "bare_api",
  "integration_name": "My API Integration",
  "permissions": {
    "can_read": true,
    "can_write": true,
    "can_search": true,
    "can_delete": false
  }
}

# 2. Get your API endpoint
GET /api/v1/socket-access/{binding_id}/info

# 3. Start making API calls
curl -X POST "https://api.memara.io/api/v1/socket-access/{binding_id}/memories" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"content": "Your memory content", "tags": ["tag1", "tag2"]}'

# 4. Search your memories
curl -X GET "https://api.memara.io/api/v1/socket-access/{binding_id}/memories/search?query=your+search+term" \\
  -H "Authorization: Bearer YOUR_API_KEY"`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=example', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="bare_api"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-50 shadow" aria-label="REST API logo">
          <Code className="w-8 h-8 text-gray-600" />
        </span>
      }
      title="REST API"
      description="Direct REST API access to your Memara memory system with Bearer token authentication. Build custom applications, mobile apps, and third-party services that can create, read, search, and manage memories programmatically."
      features={[
        { icon: <Code className="h-4 w-4 text-gray-600" />, title: 'Direct REST API Access', description: 'Full CRUD operations for memories with standard HTTP methods and JSON request/response format.' },
        { icon: <Brain className="h-4 w-4 text-gray-600" />, title: 'Semantic Search Capabilities', description: 'Advanced search with query parameters, filters, and semantic understanding of memory content.' },
        { icon: <Shield className="h-4 w-4 text-gray-600" />, title: 'Secure Authentication', description: 'Bearer token authentication with configurable permissions and rate limiting for production use.' },
        { icon: <Database className="h-4 w-4 text-gray-600" />, title: 'Memory Space Isolation', description: 'Multi-tenant architecture with secure memory space isolation and per-integration scoping.' },
        { icon: <Zap className="h-4 w-4 text-gray-600" />, title: 'High Performance', description: 'Optimized for high-throughput applications with sub-second response times and efficient memory operations.' },
        { icon: <Settings className="h-4 w-4 text-gray-600" />, title: 'Flexible Configuration', description: 'Customizable permissions, rate limits, and response formats to match your application needs.' },
      ]}
      setupSteps={[
        'Create a REST API integration in your memory space',
        'Configure permissions and rate limits for your use case',
        'Get your unique API endpoint and authentication token',
        'Start making API calls to create and retrieve memories',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-gray-600 hover:bg-gray-700 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-gray-200 text-gray-600 hover:bg-gray-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-gray-200 text-gray-600 hover:bg-gray-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Settings className="w-4 h-4 mr-2" />,
        },
      ]}
      screenshots={[
        {
          id: 'api-integration-setup',
          title: 'Create API Integration',
          description: 'Set up your REST API integration with configurable permissions and rate limits',
          placeholder: 'Integration setup modal showing API configuration options',
          badge: 'Step 1'
        },
        {
          id: 'api-endpoint-info',
          title: 'API Endpoint Information',
          description: 'Get your unique API endpoint and authentication details for integration',
          placeholder: 'API endpoint information panel showing authentication and usage details',
          badge: 'Setup'
        },
        {
          id: 'api-testing',
          title: 'Test API Calls',
          description: 'Test your integration with cURL examples and real-time API responses',
          placeholder: 'API testing interface with cURL examples and response validation',
          badge: 'Testing'
        }
      ]}
    />
  );
}
